import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useApiFetch } from "@/hooks/useApiFetch";
import { RetryState } from "@/components/LoadStates";
import { HubCardSkeletonGrid } from "@/components/Skeletons";
import ChartCover from "@/components/ChartCover";
import { track } from "@/lib/analytics";

export default function Roadmaps() {
  const fetcher = useCallback(async () => (await api.get("/roadmaps")).data, []);
  const { data: items, loading, error, retry } = useApiFetch(fetcher, { deps: [] });

  const { live, upcoming } = useMemo(() => {
    const list = items || [];
    return {
      live: list.filter((r) => r.status !== "coming_soon"),
      upcoming: list.filter((r) => r.status === "coming_soon"),
    };
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">Career Hubs</div>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-[#0A2540] font-medium mt-3">
          Explore structured Career Hubs.
        </h1>
        <p className="mt-3 text-slate-600 text-lg leading-relaxed">
          Each Career Hub gives you a structured view of a finance career — what the work involves, how to break in, which skills matter, how to prepare, and where to go deeper.
        </p>
      </div>

      {loading ? (
        <HubCardSkeletonGrid count={6} />
      ) : error ? (
        <div className="mt-10">
          <RetryState
            title="Couldn't load Career Hubs."
            body="The server may be briefly restarting. Please try again in a moment."
            onRetry={retry}
            testId="roadmaps-retry-state"
          />
        </div>
      ) : (items?.length ?? 0) === 0 ? (
        <div className="mt-14 text-slate-500 text-center">No Career Hubs available.</div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...live, ...upcoming].map((r, i) => (
            <motion.div
              key={r.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
            >
              <HubCard hub={r} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function HubCard({ hub }) {
  const isComingSoon = hub.status === "coming_soon";
  // Live hubs show top 3 skills; coming-soon hubs show preview_topics
  const chips = isComingSoon
    ? hub.preview_topics || []
    : (hub.key_skills || []).slice(0, 3);

  const CardBody = (
    <>
      <div className="relative h-40 overflow-hidden">
        <ChartCover variant={hub.chart || "line"} label={hub.slug.replace("-", " ")} slug={hub.slug} />
        <div className="absolute bottom-3 left-5 right-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/70 font-semibold">
            Career Hub
          </div>
          <h3 className="font-display text-2xl text-white mt-0.5">{hub.title}</h3>
        </div>
      </div>
      <div className="p-6">
        <p className="text-sm text-slate-600 leading-relaxed">{hub.tagline}</p>
        {chips.length > 0 && (
          <div
            className="mt-4 flex flex-wrap gap-1.5"
            data-testid={isComingSoon ? `hub-preview-chips-${hub.slug}` : undefined}
          >
            {chips.map((s) => (
              <span
                key={s}
                className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-700"
              >
                {s}
              </span>
            ))}
          </div>
        )}
        {isComingSoon ? (
          <div
            data-testid={`hub-coming-soon-${hub.slug}`}
            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Launching soon</span>
          </div>
        ) : (
          <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540]">
            Open Career Hub
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        )}
      </div>
    </>
  );

  if (isComingSoon) {
    return (
      <div
        data-testid={`roadmap-card-${hub.slug}`}
        aria-disabled
        className="group block rounded-3xl overflow-hidden border border-slate-100 bg-white cursor-default"
      >
        {CardBody}
      </div>
    );
  }

  return (
    <Link
      to={`/roadmaps/${hub.slug}`}
      data-testid={`roadmap-card-${hub.slug}`}
      onClick={() =>
        track("hub_click", {
          hub_slug: hub.slug,
          hub_title: hub.title,
          source: "roadmaps_list",
        })
      }
      className="group block rounded-3xl overflow-hidden border border-slate-100 bg-white vestryn-card-lift"
    >
      {CardBody}
    </Link>
  );
}
